import { supabase } from './client';
import { getCurrentEmpresaId } from './auth-utils';

export interface RecebimentoPedido {
  id?: string;
  order_id: string;
  empresa_id?: string;
  valor_recebido: number;
  data_recebimento: string; // ISO date (YYYY-MM-DD)
  forma_pagamento_id?: string | null;
  observacoes?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Listar recebimentos de um pedido
export async function listarRecebimentosPedido(orderId: string): Promise<RecebimentoPedido[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      console.error('Usuário não tem empresa associada');
      return [];
    }

    const { data, error } = await supabase
      .from('recebimentos_pedidos')
      .select('*')
      .eq('order_id', orderId)
      .eq('empresa_id', empresaId)
      .order('data_recebimento', { ascending: false });

    if (error) {
      console.error('Erro ao listar recebimentos:', error);
      return [];
    }

    return (data || []) as RecebimentoPedido[];
  } catch (erro) {
    console.error('Erro ao listar recebimentos:', erro);
    return [];
  }
}

// Criar recebimento
export async function criarRecebimento(input: Omit<RecebimentoPedido, 'id' | 'created_at' | 'updated_at' | 'empresa_id'>): Promise<{ ok: boolean; data?: RecebimentoPedido; error?: string }> {
  try {
    console.log(`💳 [criarRecebimento] Iniciando criação de recebimento:`, input);
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      console.error(`❌ [criarRecebimento] Usuário não tem empresa associada`);
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }
    console.log(`📍 [criarRecebimento] Empresa ID: ${empresaId}`);

    const { data, error } = await supabase
      .from('recebimentos_pedidos')
      .insert({
        order_id: input.order_id,
        empresa_id: empresaId,
        valor_recebido: input.valor_recebido,
        data_recebimento: input.data_recebimento,
        forma_pagamento_id: input.forma_pagamento_id || null,
        observacoes: input.observacoes || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error(`❌ [criarRecebimento] Erro ao inserir recebimento no banco:`, error);
      return { ok: false, error: error.message };
    }
    console.log(`✅ [criarRecebimento] Recebimento inserido com sucesso:`, data);

    // Atualizar o campo paid da order com o total recebido
    if (data && input.order_id) {
      console.log(`📊 [criarRecebimento] Buscando total de recebimentos para o pedido ${input.order_id}...`);
      const { data: recebimentos, error: recebimentosError } = await supabase
        .from('recebimentos_pedidos')
        .select('valor_recebido')
        .eq('order_id', input.order_id)
        .eq('empresa_id', empresaId);

      if (recebimentosError) {
        console.error(`❌ [criarRecebimento] Erro ao buscar recebimentos:`, recebimentosError);
      } else if (recebimentos) {
        const totalRecebido = recebimentos.reduce((sum, r) => sum + Number(r.valor_recebido || 0), 0);
        console.log(`💰 [criarRecebimento] Total recebido para o pedido: R$ ${totalRecebido.toFixed(2)}`);
        console.log(`   Número de recebimentos: ${recebimentos.length}`);

        const { error: updateError } = await supabase
          .from('atelie_orders')
          .update({ paid: totalRecebido })
          .eq('id', input.order_id);

        if (updateError) {
          console.error(`❌ [criarRecebimento] Erro ao atualizar campo 'paid' na order:`, updateError);
        } else {
          console.log(`✅ [criarRecebimento] Campo 'paid' atualizado na order para R$ ${totalRecebido.toFixed(2)}`);
        }

        // ✅ Triggers devem sincronizar automaticamente com contas_receber
        console.log(`🔄 [criarRecebimento] Triggers do banco devem sincronizar com contas_receber e atelie_receitas...`);
      }
    }

    return { ok: true, data: data as RecebimentoPedido };
  } catch (erro: any) {
    console.error(`❌ [criarRecebimento] Erro geral:`, erro);
    return { ok: false, error: erro.message || 'Erro ao criar recebimento' };
  }
}

// Atualizar recebimento
export async function atualizarRecebimento(id: string, updates: Partial<RecebimentoPedido>): Promise<{ ok: boolean; data?: RecebimentoPedido; error?: string }> {
  try {
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('recebimentos_pedidos')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar recebimento:', error);
      return { ok: false, error: error.message };
    }

    return { ok: true, data: data as RecebimentoPedido };
  } catch (erro: any) {
    console.error('Erro ao atualizar recebimento:', erro);
    return { ok: false, error: erro.message || 'Erro ao atualizar recebimento' };
  }
}

// Deletar recebimento
export async function deletarRecebimento(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    // Buscar o recebimento para saber qual order_id atualizar
    const { data: recebimento } = await supabase
      .from('recebimentos_pedidos')
      .select('order_id, empresa_id')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('recebimentos_pedidos')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao deletar recebimento:', error);
      return { ok: false, error: error.message };
    }

    // Recalcular o total recebido para a order
    if (recebimento) {
      const { data: recebimentos } = await supabase
        .from('recebimentos_pedidos')
        .select('valor_recebido')
        .eq('order_id', recebimento.order_id)
        .eq('empresa_id', recebimento.empresa_id);

      const totalRecebido = recebimentos
        ? recebimentos.reduce((sum, r) => sum + Number(r.valor_recebido || 0), 0)
        : 0;

      await supabase
        .from('atelie_orders')
        .update({ paid: totalRecebido })
        .eq('id', recebimento.order_id);

      // ✅ TODO: Sincronizar com contas_receber (será feito com trigger no banco)
    }

    return { ok: true };
  } catch (erro: any) {
    console.error('Erro ao deletar recebimento:', erro);
    return { ok: false, error: erro.message || 'Erro ao deletar recebimento' };
  }
}

// Obter total recebido de um pedido
export async function getTotalRecebidoPedido(orderId: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('recebimentos_pedidos')
      .select('valor_recebido')
      .eq('order_id', orderId);

    if (error) {
      console.error('Erro ao calcular total recebido:', error);
      return 0;
    }

    return (data || []).reduce((sum, item) => sum + (item.valor_recebido || 0), 0);
  } catch (erro) {
    console.error('Erro ao calcular total recebido:', erro);
    return 0;
  }
}

// Listar recebimentos por data (para relatório)
export async function listarRecebimentosPorData(data: string): Promise<RecebimentoPedido[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return [];
    }

    const { data: recebimentos, error } = await supabase
      .from('recebimentos_pedidos')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('data_recebimento', data)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao listar recebimentos por data:', error);
      return [];
    }

    return (recebimentos || []) as RecebimentoPedido[];
  } catch (erro) {
    console.error('Erro ao listar recebimentos por data:', erro);
    return [];
  }
}

// Listar recebimentos por período
export async function listarRecebimentosPorPeriodo(dataInicio: string, dataFim: string): Promise<RecebimentoPedido[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return [];
    }

    const { data: recebimentos, error } = await supabase
      .from('recebimentos_pedidos')
      .select('*')
      .eq('empresa_id', empresaId)
      .gte('data_recebimento', dataInicio)
      .lte('data_recebimento', dataFim)
      .order('data_recebimento', { ascending: false });

    if (error) {
      console.error('Erro ao listar recebimentos por período:', error);
      return [];
    }

    return (recebimentos || []) as RecebimentoPedido[];
  } catch (erro) {
    console.error('Erro ao listar recebimentos por período:', erro);
    return [];
  }
}
